import i18n from "@/config/i18n";
import { toDayjsLocale } from "@/utils/muiLocale";
import { DATE_FORMAT } from "@/constants";
import type { Address, Currency, ValidatedAddress } from "@/gql/graphql";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const RELATIVE_TIME_THRESHOLDS = [
  { l: "m", r: 1 },
  { l: "mm", r: 59, d: "minute" },
  { l: "h", r: 1 },
  { l: "hh", r: 23, d: "hour" },
  { l: "d", r: 1 },
  { l: "dd", d: "day" },
];

dayjs.extend(relativeTime, {
  thresholds: RELATIVE_TIME_THRESHOLDS,
  rounding: Math.floor,
});
dayjs.extend(updateLocale);

const justNow = () => i18n.t("dashboard.alerts.timing.just_now");

dayjs.updateLocale(toDayjsLocale(i18n.language), {
  relativeTime: {
    past: (out: string) => (out === justNow() ? out : `${out} ago`),
    m: () => justNow(),
    h: () => i18n.t("dashboard.alerts.timing.one_hour"),
    d: () => i18n.t("dashboard.alerts.timing.one_day"),
  },
});

const MONEY_MINIMUM_FRACTION_DIGITS = 2;

// Intl.NumberFormat is expensive to construct; cache one per locale and fraction-digit setting.
const localizedNumberFormatters = new Map<string, Intl.NumberFormat>();

const getLocalizedNumberFormatter = (
  locale: string,
  fractionDigits: number
): Intl.NumberFormat => {
  const cacheKey = `${locale}:${fractionDigits}`;
  const cachedFormatter = localizedNumberFormatters.get(cacheKey);
  if (cachedFormatter) {
    return cachedFormatter;
  }

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  localizedNumberFormatters.set(cacheKey, formatter);
  return formatter;
};

const getMoneyFormatter = (locale: string): Intl.NumberFormat =>
  getLocalizedNumberFormatter(locale, MONEY_MINIMUM_FRACTION_DIGITS);

const money = (number: number) =>
  getMoneyFormatter(i18n.language).format(number);

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$",
  EUR: "€",
};

const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_SYMBOLS[currencyCode] ?? currencyCode;
};
const currency = (number: number, currency: Pick<Currency, "id">) => {
  const localizedNumber = money(number);
  const code = currency.id;
  return `${localizedNumber} ${code}`;
};

const currencySymbol = (
  number: number | null | undefined,
  currency: Pick<Currency, "id">,
  minimumFractionDigits = MONEY_MINIMUM_FRACTION_DIGITS
) => {
  const currencyCode = currency.id;
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    AUD: "A$",
    CAD: "C$",
  };
  const symbol = symbols[currencyCode] ?? currencyCode;

  // If number is null or undefined, return only the symbol (no number)
  if (number === null || number === undefined) {
    return symbol;
  }
  const localizedNumber = getLocalizedNumberFormatter(
    i18n.language,
    minimumFractionDigits
  ).format(number);
  return `${symbol} ${localizedNumber}`;
};

const currencySymbolAndCode = (
  number: number,
  currency: Pick<Currency, "id">
) => {
  const localizedNumber = currencySymbol(number, currency);
  return `${localizedNumber} ${currency.id}`;
};

/**
 * Formats an address object into a string as follows:
 * "address1, address2, city, state zip, country"
 */
const address = (
  address:
    | Pick<
        Address | ValidatedAddress,
        | "address1"
        | "address2"
        | "city"
        | "stateDetails"
        | "zip"
        | "countryDetails"
      >
    | {
        address1?: string;
        address2?: string | null;
        city?: string;
        zip?: string;
        stateDetails?: { defaultName: string; code?: string } | null;
        countryDetails?: { defaultName: string } | null;
      }
    | null
    | undefined
) => {
  const addressParts = [
    address?.address1,
    address?.address2,
    address?.city,
    address?.stateDetails
      ? `${address.stateDetails.defaultName} ${address.zip}`
      : (address?.zip ?? ""),
    address?.countryDetails?.defaultName,
  ].filter(Boolean);

  return addressParts.join(", ")
    ? addressParts.join(", ")
    : i18n.t("contract_summary.customer_card.unknown");
};

const notificationUri = (uri: string) => uri.replace("dxp://accounts/", "/");

// dayjs's global locale is updated after languageChanged. Bind the instance
// so month names and relative phrases follow i18n on that render.
const withActiveLocale = (value: ReturnType<typeof dayjs>) =>
  value.locale(toDayjsLocale(i18n.resolvedLanguage ?? i18n.language));

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "";
  }
  const input = dayjs.utc(dateString);
  if (!input.isValid()) {
    return "";
  }
  return withActiveLocale(input).format(DATE_FORMAT.DATE);
};

const timeAgo = (date: string) => {
  const now = dayjs();
  const input = dayjs(date);
  const diffInHours = now.diff(input, "hour");

  if (diffInHours < 24) {
    return input.fromNow();
  }
  return input.format(DATE_FORMAT.DATE);
};

const RELATIVE_DAYS_THRESHOLD = 7;

const alertTimestamp = (date: string | null | undefined): string => {
  if (!date) {
    return "";
  }
  const now = dayjs();
  const input = withActiveLocale(dayjs(date));

  if (!input.isValid()) {
    return "";
  }

  if (now.diff(input, "day") > RELATIVE_DAYS_THRESHOLD) {
    return input.format(DATE_FORMAT.DATE);
  }

  return input.fromNow();
};

const format = {
  money,
  timeAgo,
  alertTimestamp,
  currency,
  currencySymbol,
  currencySymbolAndCode,
  address,
  notificationUri,
  formatDate,
  getCurrencySymbol,
};

export { format };
